import Course from "../types/Course";
import Subject, {subjectNames} from "../util/Subject";
import {FCWithHTMLAttributes} from "../types/reactTypes";
import clsMerge from "../util/clsMerge";
import {ReactNode} from "react";
import {CourseLevel} from "../types/CourseLevel";
import CourseButtonGrid from "./CourseButtonGrid";
import OverlineWithLine from "./decorations/OverlineWithLine";
import {TextContentNode} from "./cms/CmsTypes";
import TextSection from "./cms/TextSection";
import {StrapiImageData} from "./StrapiImage";
import UpperCatalogImage from "../(withHeaderAndFooter)/(sellPages)/courses/_components/UpperCatalogImage";
import LowerCatalogImageOnBackground from "../(withHeaderAndFooter)/(sellPages)/courses/_components/LowerCatalogImageOnBackground";
import LowerCatalogFillImage from "../(withHeaderAndFooter)/(sellPages)/courses/_components/LowerCatalogFillImage";
import OverlapCatalogImage from "../(withHeaderAndFooter)/(sellPages)/courses/_components/OverlapCatalogImage";
import Container from "./Container";
import CourseShelfDecorations, {SUBJECT_STYLES} from "./CourseShelfDecorations";

interface CourseShelfProps {
	blurb?: TextContentNode[];
	subject: Subject;
	courseLevel?: CourseLevel;
	courseNoun?: string;
	courses: Course[];
	title?: string;
	sideBoxContent?: ReactNode;
	image?: StrapiImageData;
	imageMd?: StrapiImageData;
	imageLg?: StrapiImageData;
}

export const getShelfId = (subject: Subject) => `shelf-${subject}`;

type CatalogImageClassProps = {
	imageClass?: string;
	imageClassMd?: string;
	imageClassLg?: string;
};

export type CatalogImageProps = {
	image: StrapiImageData;
	imageMd: StrapiImageData;
	imageLg: StrapiImageData;
	subject: Subject;
} & CatalogImageClassProps;

const subjectCourseLevelToImageComponent: Record<
	Subject,
	Partial<Record<CourseLevel, FCWithHTMLAttributes<CatalogImageProps> | null>>
> = {
	[Subject.LANGUAGE_ARTS]: {
		[CourseLevel.MIDDLE_AND_HIGH]: LowerCatalogImageOnBackground,
		[CourseLevel.ELEMENTARY]: UpperCatalogImage,
	},
	[Subject.MATH]: {
		[CourseLevel.MIDDLE_AND_HIGH]: LowerCatalogFillImage,
		[CourseLevel.ELEMENTARY]: UpperCatalogImage,
	},
	[Subject.SCIENCE]: {
		[CourseLevel.UNDEFINED]: UpperCatalogImage,
	},
	[Subject.COMPUTER_SCIENCE]: {
		[CourseLevel.UNDEFINED]: OverlapCatalogImage,
	},
};

const subjectCourseLevelToImageCustomClasses: Record<
	Subject,
	Partial<Record<CourseLevel, CatalogImageClassProps>>
> = {
	// Hint intellisense that these strings are tailwind
	/* tailwind-until-semicolon */
	[Subject.LANGUAGE_ARTS]: {
		[CourseLevel.ELEMENTARY]: {
			imageClass: "w-[20rem]",
			imageClassMd: "w-[20.79rem]",
		},
		[CourseLevel.MIDDLE_AND_HIGH]: {
			imageClass: "w-[21.23rem]",
			imageClassMd: "w-[15.42rem]",
			imageClassLg: "w-[23.9rem]",
		},
	},
	[Subject.MATH]: {
		[CourseLevel.ELEMENTARY]: {
			imageClass: "w-[23.44rem]",
			imageClassMd: "w-[28.13rem]",
			imageClassLg: "w-[23.44rem]",
		},
		[CourseLevel.MIDDLE_AND_HIGH]: {
			imageClassLg: "w-[23.9rem]",
		},
	},
	[Subject.SCIENCE]: {
		[CourseLevel.UNDEFINED]: {
			imageClass: "w-[18.31rem]",
			imageClassMd: "w-[24.37rem]",
		},
	},
	[Subject.COMPUTER_SCIENCE]: {
		[CourseLevel.UNDEFINED]: {
			imageClass: "w-[13.71rem]",
			imageClassMd: "w-[18.67rem]",
			imageClassLg: "w-[30.47rem]",
		},
	},
};

const getImageComponent = (subject: Subject, courseLevel: CourseLevel) =>
	subjectCourseLevelToImageComponent[subject][courseLevel] ?? null;

const getImageCustomClasses = (subject: Subject, courseLevel: CourseLevel) =>
	subjectCourseLevelToImageCustomClasses[subject][courseLevel] ?? null;

const CourseShelf: FCWithHTMLAttributes<CourseShelfProps> = ({
	blurb,
	className,
	subject,
	courseLevel,
	courseNoun = "Courses",
	courses,
	sideBoxContent,
	title,
	image,
	imageMd,
	imageLg,
	...props
}) => {
	const visibleCourses = courseLevel
		? courses.filter((course) => course.courseLevel === courseLevel)
		: courses;
	if (visibleCourses.length === 0) {
		return null;
	}

	const areImagesSet = image && imageMd && imageLg;
	const definedCourseLevel = courseLevel ?? CourseLevel.UNDEFINED;
	const ImageComponent = getImageComponent(subject, definedCourseLevel);
	const imageClasses = getImageCustomClasses(subject, definedCourseLevel);

	const usingLowerImage =
		ImageComponent === LowerCatalogFillImage ||
		ImageComponent === LowerCatalogImageOnBackground;
	const decorationStyles =
		definedCourseLevel === CourseLevel.MIDDLE_AND_HIGH
			? SUBJECT_STYLES[subject]
			: null;

	return (
		<Container
			id={getShelfId(subject)}
			className={clsMerge(
				`relative flex scroll-mt-12 flex-col gap-10 overflow-x-visible xl:flex-row xl:justify-between xl:gap-36`,
				className
			)}
			outerClassName={clsMerge(
				"relative overflow-clip",
				decorationStyles?.background
			)}
			{...props}
		>
			{decorationStyles && <CourseShelfDecorations subject={subject} />}
			{areImagesSet && ImageComponent && (
				<ImageComponent
					image={image}
					imageMd={imageMd}
					imageLg={imageLg}
					subject={subject}
					className={clsMerge(
						"xl:order-2 xl:shrink-0 xl:self-center",
						usingLowerImage ? "hidden xl:block" : ""
					)}
					{...imageClasses}
				/>
			)}
			<div className="xl:w-[37.5rem] xl:grow">
				<div
					className={
						usingLowerImage
							? "pb-10 md:flex md:flex-row-reverse md:justify-between md:gap-6 xl:flex-none xl:pb-0"
							: ""
					}
				>
					<div>
						<OverlineWithLine
							className="mb-3 text-sm text-gray-600"
							overline={courseLevel}
						/>
						<h2 className="mb-4 text-pretty text-h1 leading-[1.2]">
							{title ?? `Explore ${subjectNames[subject]} ${courseNoun}`}
						</h2>
						{blurb && (
							<div className="mb-6">
								<TextSection contents={blurb} />
							</div>
						)}
						{sideBoxContent && (
							<div className="mb-6 bg-gray-200 p-4">{sideBoxContent}</div>
						)}
					</div>
					{areImagesSet && ImageComponent && usingLowerImage && (
						<ImageComponent
							image={image}
							imageMd={imageMd}
							imageLg={imageLg}
							subject={subject}
							className={"flex-shrink-0 md:w-[20.3rem] xl:hidden"}
							{...imageClasses}
						/>
					)}
				</div>
				<CourseButtonGrid courses={visibleCourses} />
			</div>
		</Container>
	);
};

export default CourseShelf;
